import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import AppDropdown from "../../components/AppDropdown/AppDropdown";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import EmployeeSelect from "./EmployeeSelect";
import { Field, PrimaryButton, SecondaryButton } from "./hrComponents";
import { inputStyle } from "./hrUi";
import { getVendorCurrency } from "../../utils/currency";
import { phoneCountryIso } from "../../utils/phoneValidation";

const DIAL_CODES = ["971", "91"];
const onlyDigits = (x) => String(x === undefined || x === null ? "" : x).replace(/\D/g, "");

function MobileInput({ id, name, value, onChange, disabled }) {
  return (
    <PhoneInput
      country={phoneCountryIso(getVendorCurrency())}
      onlyCountries={["ae", "in"]}
      value={onlyDigits(value)}
      disabled={disabled}
      onChange={(next) => {
        const digits = onlyDigits(next);
        // Clearing the box leaves only the dial code behind: that is "no number". An unchanged value is not a change.
        const cleaned = DIAL_CODES.includes(digits) ? "" : digits;
        if (cleaned !== onlyDigits(value)) onChange(cleaned);
      }}
      inputStyle={{ width: "100%", height: inputStyle.height, fontSize: inputStyle.fontSize, backgroundColor: "white", borderColor: "#E9ECEF" }}
      inputProps={{ id, name }}
    />
  );
}

// A small descriptor-driven form. Each field: { key, label, type, options?, required?, hint?, max?, step? }
// type: text | textarea | date | number | select | checkbox | employee | phone | mobile | email
// `mobile` is the platform's own phone input (country code + digits, UAE / India, defaulting to the company's
// country) - the same one the booking screen uses. Its value is digits only, e.g. 971501234567.
// The server validates everything; this only collects and shows its message next to the form.
// `idPrefix` keeps the element ids unique when several forms are on the page (each drawer is mounted even while closed),
// so a label always points at its own input.
export function FormFields({ fields, values, onChange, columns = 1, disabled = false, idPrefix = "f-" }) {
  const cell = (f) => {
    const v = values[f.key];
    const common = { disabled: disabled || f.disabled, id: `${idPrefix}${f.key}` };
    let control;
    switch (f.type) {
      case "textarea":
        control = <textarea {...common} className="form-control" rows={f.rows || 3} maxLength={f.max} value={v ?? ""} onChange={(e) => onChange(f.key, e.target.value)} />;
        break;
      case "select":
        control = <AppDropdown id={common.id} value={v ?? null} options={f.options || []} onChange={(e) => onChange(f.key, e.value ?? null)} placeholder={f.placeholder || "Select"} disabled={common.disabled} filter={(f.options || []).length > 8} />;
        break;
      case "mobile":
        control = <MobileInput id={common.id} name={f.key} value={v} onChange={(next) => onChange(f.key, next)} disabled={common.disabled} />;
        break;
      case "employee":
        control = <EmployeeSelect value={v} onChange={(id) => onChange(f.key, id)} exclude={f.exclude} disabled={common.disabled} placeholder={f.placeholder} />;
        break;
      case "checkbox":
        return (
          <div className="form-check mb-3" key={f.key}>
            <input {...common} type="checkbox" className="form-check-input" checked={!!v} onChange={(e) => onChange(f.key, e.target.checked)} />
            <label htmlFor={common.id} className="form-check-label" style={{ fontSize: 14 }}>
              {f.label}
            </label>
          </div>
        );
      default:
        control = (
          <input
            {...common}
            type={f.type === "phone" ? "tel" : f.type || "text"}
            className="form-control"
            style={inputStyle}
            maxLength={f.max}
            step={f.step}
            min={f.min}
            value={v ?? ""}
            onChange={(e) => onChange(f.key, e.target.value)}
          />
        );
    }
    return (
      <Field key={f.key} label={f.label} required={f.required} hint={f.hint} htmlFor={common.id}>
        {control}
      </Field>
    );
  };

  if (columns === 1) return <>{fields.map(cell)}</>;
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: "0 16px" }} className="hr-form-grid">
      {fields.map(cell)}
    </div>
  );
}

// Empty strings become undefined so the API treats them as "not given".
export const cleanValues = (values) => {
  const out = {};
  Object.entries(values).forEach(([k, v]) => {
    if (v === "" || v === null || v === undefined) return;
    out[k] = typeof v === "string" ? v.trim() : v;
  });
  return out;
};

// For edits: every listed field, with a cleared one sent as null so the API clears it
// (cleanValues would drop it and leave the old value in place).
export const withNulls = (raw, keys) =>
  Object.fromEntries(keys.map((k) => [k, raw[k] === undefined || raw[k] === null || (typeof raw[k] === "string" && raw[k].trim() === "") ? null : typeof raw[k] === "string" ? raw[k].trim() : raw[k]]));

/* ------------------------------ masters as options ------------------------------ */

const masterCache = {};
export const resetMasterCache = () => Object.keys(masterCache).forEach((k) => delete masterCache[k]);

const toOption = (key, row) => ({ label: key === "cost-centres" ? `${row.code} - ${row.name}` : row.name, value: row.id });

// The active rows of the organisation masters (and leave policies) as dropdown options,
// loaded once per page load: { departments: [{label,value}], ... }.
export function useMasterOptions(keys) {
  const [state, setState] = useState({});
  const wanted = keys.join(",");
  useEffect(() => {
    let alive = true;
    Promise.all(
      wanted.split(",").map(async (key) => {
        if (!masterCache[key]) {
          const load = key === "leave-policies" ? hrmsService.listLeavePolicies() : hrmsService.listMaster(key, { status: "ACTIVE" });
          masterCache[key] = load.then((res) => (Array.isArray(res?.data) ? res.data : [])).catch(() => []);
        }
        return [key, (await masterCache[key]).map((r) => toOption(key, r))];
      })
    ).then((pairs) => alive && setState(Object.fromEntries(pairs)));
    return () => {
      alive = false;
    };
  }, [wanted]);
  return state;
}

/* ------------------------------ a save-drawer around FormFields ------------------------------ */

let drawerCounter = 0;

// `fields` is a list, or a function of the current values (for forms whose fields depend on a choice).
// Opens with `initial`, collects values, and calls `onSave(cleanValues)` which resolves with the API answer
// ({ success, message }). Closes and calls `onDone` on success; shows the API's message otherwise.
export function FormDrawer({ show, title, fields, initial, onHide, onSave, onDone, width = 520, submitLabel = "Save", columns = 1, children }) {
  // The form starts from `initial` each time it opens. Done while rendering (React's "derived state" pattern) rather than
  // in an effect, so there is never a moment when it is open but still holds the previous values.
  const [values, setValues] = useState(initial || {});
  const [wasShown, setWasShown] = useState(show);
  if (show !== wasShown) {
    setWasShown(show);
    if (show) setValues(initial || {});
  }
  const [saving, setSaving] = useState(false);
  const [uid] = useState(() => ++drawerCounter);

  const save = async () => {
    setSaving(true);
    try {
      const res = await onSave(cleanValues(values), values);
      if (res?.success) {
        toast.success(res.message || "Saved");
        onDone && onDone(res.data);
        onHide();
      } else toast.error(hrmsService.responseMessage(res));
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={title}
      width={width}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            {submitLabel}
          </PrimaryButton>
        </>
      }
    >
      <FormFields idPrefix={`fd${uid}-`} columns={columns} values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={typeof fields === "function" ? fields(values) : fields} />
      {typeof children === "function" ? children(values, setValues) : children}
    </SlidePanel>
  );
}
